/**
 * Naam Darbar — Submit FAB (Floating Action Button)
 *
 * A persistent FAB in the bottom-right corner for quick samagam submission.
 * Opens a popover with three options: WhatsApp, paste text, or upload poster.
 * The text/image modals reuse existing widgets.
 */

import { memo, lazy, Suspense, useState, useRef } from 'react';
import { Plus, X, MessageCircle, FileText, Image } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Lazy — both widgets (and their reCAPTCHA plumbing) only load when the
// user actually opens a submit modal, keeping them out of the layout chunk.
const TextSubmitWidget = lazy(() => import('../upload/TextSubmitWidget'));
const ImageUploadWidget = lazy(() => import('../upload/ImageUploadWidget'));

type ModalView = null | 'text' | 'image';

function SubmitFAB() {
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';

  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState<ModalView>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);
  useEscapeKey(() => {
    setMenuOpen(false);
    setModal(null);
  }, menuOpen || modal !== null);

  function openModal(view: ModalView) {
    setMenuOpen(false);
    setModal(view);
  }

  const actions = [
    {
      label: 'WhatsApp',
      icon: MessageCircle,
      color: isSukhasan
        ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60'
        : 'bg-green-50 text-green-700 hover:bg-green-100',
      onClick: () => {
        setMenuOpen(false);
        window.open('https://wa.me/917900270013', '_blank');
      },
    },
    {
      label: 'Paste Text',
      icon: FileText,
      color: isSukhasan
        ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60'
        : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
      onClick: () => openModal('text'),
    },
    {
      label: 'Upload Poster',
      icon: Image,
      color: isSukhasan
        ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60'
        : 'bg-purple-50 text-purple-700 hover:bg-purple-100',
      onClick: () => openModal('image'),
    },
  ];

  return (
    <>
      {/* ── FAB + Popover ──────────────────────────────────────── */}
      <div ref={menuRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Popover menu */}
        {menuOpen && (
          <div className="card-glass rounded-2xl p-2 shadow-xl backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2 duration-200">
            <p className="px-3 pt-1.5 pb-2 text-xs font-semibold tracking-wide uppercase text-tx-muted">
              Submit Samagam
            </p>
            <div className="flex flex-col gap-1">
            {actions.map((action) => (
              <button
                key={action.label}
                onClick={action.onClick}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  action.color,
                )}
              >
                <action.icon size={18} />
                {action.label}
              </button>
            ))}
            </div>
          </div>
        )}

        {/* FAB button */}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Submit a samagam"
          className="flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-95 bg-gradient-to-br from-accent-600 to-accent-500"
          style={{ color: 'rgb(var(--cta-text))' }}
        >
          {menuOpen ? (
            <X size={24} className="transition-transform duration-200" />
          ) : (
            <Plus size={24} className="transition-transform duration-200" />
          )}
        </button>
      </div>

      {/* ── Modal overlay ──────────────────────────────────────── */}
      {modal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setModal(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal card — the widget owns its own close (X) button */}
          <div
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Suspense
              fallback={
                <div className="card-glass flex items-center justify-center rounded-2xl p-12">
                  <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                </div>
              }
            >
              {modal === 'text' && <TextSubmitWidget onClose={() => setModal(null)} />}
              {modal === 'image' && <ImageUploadWidget onClose={() => setModal(null)} />}
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
}

// No props + theme comes from context → safe to memo; skips the re-render
// Layout triggers on every route change / auth update.
export default memo(SubmitFAB);
